// -----------------------------------------------------------------------------
// Right-hand pane of the chat page — channel header (title, members pill,
// search, mute toggle, member avatars) followed by the scrollable message
// list and the bottom composer.
//
// Scroll behaviour: snaps to the latest message on channel switch, on your
// own sends, and on inbound messages while you're already at the bottom.
// While you're reading history, inbound messages instead light up a
// floating "jump to latest" pill with a count. Reaching the top of the
// scroll auto-loads the previous page of history (the button stays as a
// fallback), keeping the viewport anchored while older rows prepend.
// -----------------------------------------------------------------------------

import { useLayoutEffect, useRef, useState } from "react";
import {
  ArrowDownIcon,
  BellAlertIcon,
  BellSlashIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { channelLabel, fullNameFor } from "../../utils/chatHelpers";
import { useChatMessageSearch } from "../../data/useChat";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import Composer from "./Composer";
import MessageList, { scrollToMessage } from "./MessageList";
import { formatTime, parseServerIso } from "./chatTime";

/** How close to the bottom (px) still counts as "reading the latest". */
const AT_BOTTOM_THRESHOLD = 80;
/** How close to the top (px) triggers an automatic older-page load. */
const AUTO_LOAD_THRESHOLD = 60;

interface ChannelThreadProps {
  channel: IChatChannel;
  me: IChatUser;
  usersById: Map<string, IChatUser>;
  messages: IChatMessage[];
  loading: boolean;
  hasMore?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  sending?: boolean;
  /** Ids of other members currently typing (from the SignalR hub). */
  typingUserIds?: string[];
  replyTo?: IChatMessage | null;
  onReply?: (message: IChatMessage) => void;
  onCancelReply?: () => void;
  onTyping?: () => void;
  onSend: (body: string, files: File[]) => void;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
  onToggleMute: (channelId: string, muted: boolean) => void;
  onManageMembers: () => void;
}

export default function ChannelThread({
  channel,
  me,
  usersById,
  messages,
  loading,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  sending = false,
  typingUserIds = [],
  replyTo = null,
  onReply,
  onCancelReply,
  onTyping,
  onSend,
  onEdit,
  onReact,
  onToggleMute,
  onManageMembers,
}: ChannelThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll handling modes:
  //  - channel switch / own send / new message while at the bottom: snap
  //    to the latest message;
  //  - new message while reading history: leave the viewport alone and
  //    bump the "jump to latest" counter instead;
  //  - older history prepended via "load more": keep the viewport
  //    anchored on the message the user was looking at (we compensate
  //    scrollTop by however much taller the list got).
  const lastMessageIdRef = useRef<string | null>(null);
  const channelIdRef = useRef<string | null>(null);
  const preserveScrollRef = useRef<number | null>(null);
  const atBottomRef = useRef(true);
  const [newWhileScrolled, setNewWhileScrolled] = useState(0);
  const [showJump, setShowJump] = useState(false);

  const handleLoadOlder = () => {
    if (!onLoadOlder || loadingOlder) return;
    preserveScrollRef.current = scrollRef.current?.scrollHeight ?? null;
    onLoadOlder();
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewWhileScrolled(0);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < AT_BOTTOM_THRESHOLD;
    atBottomRef.current = atBottom;
    setShowJump(!atBottom && distanceFromBottom > 300);
    if (atBottom) setNewWhileScrolled(0);

    // Auto-load the previous page as the user nears the top. The guard
    // against loadingOlder stops a scroll-event burst firing duplicates.
    if (el.scrollTop < AUTO_LOAD_THRESHOLD && hasMore && !loadingOlder) {
      handleLoadOlder();
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const last = messages.length ? messages[messages.length - 1] : null;
    const lastId = last?.id ?? null;
    const previousHeight = preserveScrollRef.current;
    preserveScrollRef.current = null;

    const channelChanged = channel.id !== channelIdRef.current;
    const newLatest = lastId !== lastMessageIdRef.current;

    if (previousHeight !== null && !newLatest && !channelChanged) {
      // Older history was prepended - hold the viewport in place.
      el.scrollTop += el.scrollHeight - previousHeight;
    } else if (channelChanged) {
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
      setNewWhileScrolled(0);
      setShowJump(false);
    } else if (newLatest) {
      const isOwn = last?.authorId === me.id;
      if (atBottomRef.current || isOwn) {
        el.scrollTop = el.scrollHeight;
      } else {
        setNewWhileScrolled((count) => count + 1);
      }
    }
    // (edits/reactions re-render the list without moving the scroll)

    channelIdRef.current = channel.id;
    lastMessageIdRef.current = lastId;
  }, [channel.id, messages, me.id]);

  // ----- In-channel search -----
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchQuery = useChatMessageSearch(
    searchOpen ? channel.id : null,
    searchTerm
  );
  const searchResults = searchQuery.data?.data ?? [];

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
  };

  const typingNames = typingUserIds
    .map((id) => usersById.get(id))
    .filter((u): u is IChatUser => !!u)
    .map((u) => u.forename);

  const headerTitle = channelLabel(channel, me.id, usersById);

  const otherMembers = channel.memberIds
    .map((id) => usersById.get(id))
    .filter((u): u is IChatUser => !!u);

  return (
    <>
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {channel.kind === "channel" ? (
              <HashtagIcon className="h-5 w-5 text-gray-400" />
            ) : null}
            <h2 className="truncate text-base font-semibold text-gray-900">
              {channel.kind === "channel" ? channel.name : headerTitle}
            </h2>
            {channel.kind === "channel" ? (
              <button
                type="button"
                onClick={onManageMembers}
                title="Manage members"
                className="group inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 hover:bg-primary-50 hover:text-primary-700"
              >
                <span>{channel.memberIds.length} members</span>
                <PencilSquareIcon className="h-3 w-3 opacity-60 group-hover:opacity-100" />
              </button>
            ) : (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Direct message
              </span>
            )}
          </div>
          {channel.description ? (
            <p className="truncate text-xs text-gray-500">
              {channel.description}
            </p>
          ) : channel.kind === "dm" ? (
            <p className="truncate text-xs text-gray-500">
              {otherMembers
                .filter((u) => u.id !== me.id)
                .map((u) => u.role || "Team")
                .join(" · ")}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
            title="Search this conversation"
            className={classNames(
              "inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-500 hover:bg-gray-100",
              searchOpen
                ? "border-primary-300 bg-primary-50 text-primary-700"
                : "border-gray-200 bg-white"
            )}
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onToggleMute(channel.id, !channel.muted)}
            title={
              channel.muted
                ? "Unmute — count toward total again"
                : "Mute — keep badges but exclude from total"
            }
            className={classNames(
              "inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-500 hover:bg-gray-100",
              channel.muted
                ? "border-gray-200 bg-gray-100 text-gray-700"
                : "border-gray-200 bg-white"
            )}
          >
            {channel.muted ? (
              <BellSlashIcon className="h-4 w-4" />
            ) : (
              <BellAlertIcon className="h-4 w-4" />
            )}
          </button>
          <MemberAvatars members={otherMembers} max={5} />
        </div>
      </header>

      {searchOpen ? (
        <SearchPanel
          term={searchTerm}
          onTerm={setSearchTerm}
          results={searchResults}
          searching={searchQuery.isFetching}
          usersById={usersById}
          onClose={closeSearch}
        />
      ) : null}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 space-y-1 overflow-y-auto bg-gray-50/60 px-4 py-4"
        >
          {loading && messages.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              Loading messages…
            </div>
          ) : (
            <>
              {hasMore ? (
                <div className="flex justify-center pb-1 pt-0.5">
                  <button
                    type="button"
                    onClick={handleLoadOlder}
                    disabled={loadingOlder}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-60"
                  >
                    {loadingOlder ? "Loading…" : "Load older messages"}
                  </button>
                </div>
              ) : null}
              <MessageList
                messages={messages}
                usersById={usersById}
                meId={me.id}
                onEdit={onEdit}
                onReact={onReact}
                onReply={onReply}
              />
            </>
          )}
        </div>

        {showJump || newWhileScrolled > 0 ? (
          <button
            type="button"
            onClick={scrollToBottom}
            className={classNames(
              "absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg",
              newWhileScrolled > 0
                ? "bg-primary-600 text-white hover:bg-primary-500"
                : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            )}
          >
            <ArrowDownIcon className="h-3.5 w-3.5" />
            {newWhileScrolled > 0
              ? `${newWhileScrolled} new message${newWhileScrolled === 1 ? "" : "s"}`
              : "Jump to latest"}
          </button>
        ) : null}
      </div>

      {typingNames.length > 0 ? (
        <div className="border-t border-gray-100 bg-white px-4 py-1 text-[11px] italic text-gray-500">
          {typingNames.length === 1
            ? `${typingNames[0]} is typing…`
            : typingNames.length === 2
              ? `${typingNames[0]} and ${typingNames[1]} are typing…`
              : "Several people are typing…"}
        </div>
      ) : null}

      <Composer
        channel={channel}
        usersById={usersById}
        meId={me.id}
        sending={sending}
        replyTo={replyTo}
        onCancelReply={onCancelReply}
        onTyping={onTyping}
        onSend={onSend}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// In-channel search - a slide-down panel under the header. Results are
// newest-first; clicking one jumps to the message when it's already
// loaded in the thread (older matches may need "load more" first).
// ---------------------------------------------------------------------------
function SearchPanel({
  term,
  onTerm,
  results,
  searching,
  usersById,
  onClose,
}: {
  term: string;
  onTerm: (value: string) => void;
  results: IChatMessage[];
  searching: boolean;
  usersById: Map<string, IChatUser>;
  onClose: () => void;
}) {
  const trimmed = term.trim();

  return (
    <div className="border-b border-gray-200 bg-white px-4 py-2 shadow-sm">
      <div className="flex items-center gap-2">
        <label className="flex flex-1 items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            type="text"
            autoFocus
            value={term}
            onChange={(e) => onTerm(e.target.value)}
            placeholder="Search this conversation…"
            className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
          />
        </label>
        <button
          type="button"
          onClick={onClose}
          title="Close search"
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {trimmed.length >= 2 ? (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-gray-100">
          {searching && results.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400">
              No messages match “{trimmed}”.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {results.map((result) => {
                const author = usersById.get(result.authorId);
                return (
                  <li key={result.id}>
                    <button
                      type="button"
                      onClick={() => scrollToMessage(result.id)}
                      title="Jump to message (loads only if already in view history)"
                      className="block w-full px-3 py-1.5 text-left hover:bg-gray-50"
                    >
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {author ? fullNameFor(author) : "Unknown"}
                        </span>
                        <span className="shrink-0 text-[10px] text-gray-400">
                          {parseServerIso(result.createdAt).toFormat("d LLL")}{" "}
                          {formatTime(result.createdAt)}
                        </span>
                      </span>
                      <span className="line-clamp-2 break-words text-xs text-gray-600">
                        {result.body}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : trimmed.length === 1 ? (
        <p className="mt-1.5 px-1 text-[11px] text-gray-400">
          Type at least two characters to search.
        </p>
      ) : null}
    </div>
  );
}

function MemberAvatars({
  members,
  max = 5,
}: {
  members: IChatUser[];
  max?: number;
}) {
  const shown = members.slice(0, max);
  const remaining = Math.max(0, members.length - shown.length);
  return (
    <div className="flex -space-x-2">
      {shown.map((m) => (
        <ChatAvatar
          key={m.id}
          user={m}
          size="xs"
          className="ring-2 ring-white"
        />
      ))}
      {remaining > 0 ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700 ring-2 ring-white">
          +{remaining}
        </span>
      ) : null}
    </div>
  );
}
